import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, RotateCcw, ExternalLink, Plane, Loader2, Plus, Trash2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { logger } from '../../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Flat fields grouped into sections.
const TEXT_SECTIONS = [
  { title: 'Cover', keys: [
    { k: 'guide_title', label: 'Guide title' },
    { k: 'guide_subtitle', label: 'Subtitle' },
    { k: 'guide_intro', label: 'Intro paragraph', multi: true, rows: 4 },
  ]},
  { title: 'Fast facts (3 tiles)', keys: [
    { k: 'fact_1_title', label: 'Fact 1 · title' }, { k: 'fact_1_body', label: 'Fact 1 · body', multi: true, rows: 2 },
    { k: 'fact_2_title', label: 'Fact 2 · title' }, { k: 'fact_2_body', label: 'Fact 2 · body', multi: true, rows: 2 },
    { k: 'fact_3_title', label: 'Fact 3 · title' }, { k: 'fact_3_body', label: 'Fact 3 · body', multi: true, rows: 2 },
  ]},
  { title: 'Lead-time cases', keys: [
    { k: 'lead_time_title', label: 'Section title' },
    { k: 'lead_case_a_headline', label: 'Case A · headline' },
    { k: 'lead_case_a_condition', label: 'Case A · condition' },
    { k: 'lead_case_a_notes', label: 'Case A · notes', multi: true, rows: 2 },
    { k: 'lead_case_b_headline', label: 'Case B · headline' },
    { k: 'lead_case_b_condition', label: 'Case B · condition' },
    { k: 'lead_case_b_notes', label: 'Case B · notes', multi: true, rows: 2 },
  ]},
  { title: 'Booking logistics', keys: [
    { k: 'booking_title', label: 'Section title' },
    { k: 'booking_intro', label: 'Intro', multi: true, rows: 2 },
  ]},
  { title: 'UK broker', keys: [
    { k: 'broker_title', label: 'Section title' },
    { k: 'broker_intro', label: 'Intro paragraph', multi: true, rows: 3 },
    { k: 'broker_note', label: 'Footnote', multi: true, rows: 2 },
  ]},
  { title: 'Day of travel', keys: [{ k: 'day_title', label: 'Section title' }] },
  { title: 'Supply list', keys: [
    { k: 'supplies_title', label: 'Section title' },
    { k: 'supplies_intro', label: 'Intro', multi: true, rows: 2 },
  ]},
  { title: 'Closing + contact', keys: [
    { k: 'closing_note', label: 'Closing note', multi: true, rows: 3 },
    { k: 'contact_name', label: 'Contact name' },
    { k: 'contact_role', label: 'Contact role' },
    { k: 'contact_website', label: 'Website' },
    { k: 'contact_email', label: 'Email' },
    { k: 'contact_phone', label: 'Phone' },
  ]},
];

const REPEATABLE = [
  { key: 'timeline_items',  title: 'Timeline rows',     shape: { when: '', step: '', detail: '' }, cols: ['when', 'step', 'detail'] },
  { key: 'booking_items',   title: 'Booking rows',      shape: { heading: '', body: '' },           cols: ['heading', 'body'] },
  { key: 'broker_recs',     title: 'Broker recommendations', shape: { name: '', note: '' },        cols: ['name', 'note'] },
  { key: 'day_items',       title: 'Day-of-travel rows', shape: { when: '', step: '', detail: '' }, cols: ['when', 'step', 'detail'] },
  { key: 'supplies_items',  title: 'Supply rows',       shape: { heading: '', body: '' },           cols: ['heading', 'body'] },
];

export const CaymanToUKGuideEditor = () => {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const auth = { Authorization: `Basic ${btoa(':' + password)}` };
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [previewKey, setPreviewKey] = useState(0);

  useEffect(() => {
    if (!password) { navigate('/admin/login'); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/settings/cayman-to-uk-guide`, { headers: auth });
        setContent(data.content);
      } catch (e) { toast.error('Failed to load'); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (k, v) => setContent((p) => ({ ...p, [k]: v }));
  const setListItem = (listKey, i, field, v) => setContent((p) => {
    const items = [...(p[listKey] || [])];
    items[i] = { ...items[i], [field]: v };
    return { ...p, [listKey]: items };
  });
  const addItem = (listKey, shape) => setContent((p) => ({ ...p, [listKey]: [...(p[listKey] || []), { ...shape }] }));
  const removeItem = (listKey, i) => setContent((p) => ({ ...p, [listKey]: p[listKey].filter((_, idx) => idx !== i) }));

  // Docs items is list of strings
  const setDocItem = (i, v) => setContent((p) => { const items = [...(p.docs_items || [])]; items[i] = v; return { ...p, docs_items: items }; });
  const addDocItem = () => setContent((p) => ({ ...p, docs_items: [...(p.docs_items || []), ''] }));
  const removeDocItem = (i) => setContent((p) => ({ ...p, docs_items: p.docs_items.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/cayman-to-uk-guide`, content, { headers: auth });
      toast.success('Guide saved — preview refreshed');
      setPreviewKey((k) => k + 1);
    } catch (e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const resetAll = async () => {
    if (!window.confirm('Revert every change back to defaults?')) return;
    try {
      const { data } = await axios.post(`${API}/admin/settings/cayman-to-uk-guide/reset`, {}, { headers: auth });
      setContent(data.content);
      setPreviewKey((k) => k + 1);
      toast.success('Reverted');
    } catch (e) { toast.error('Reset failed'); }
  };

  const previewUrl = useMemo(
    () => `${process.env.REACT_APP_BACKEND_URL}/api/brand/cayman-to-uk-guide.pdf?v=${previewKey}`,
    [previewKey],
  );

  if (loading || !content) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center text-foreground/60">
      <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="uk-guide-editor">
      <div className="bg-white border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/dashboard')} className="p-2 rounded-lg hover:bg-gray-100"><ArrowLeft className="w-5 h-5" /></button>
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center"><Plane className="w-5 h-5 text-primary" /></div>
            <div>
              <h1 className="text-base sm:text-xl font-bold text-primary">Cayman → UK Guide</h1>
              <p className="text-xs text-foreground/60">3-page client guide · PDF + shareable web page</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <a href="/guides/cayman-to-uk" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50" data-testid="uk-guide-open-web">
              <ExternalLink className="w-4 h-4" /> Web page
            </a>
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50" data-testid="uk-guide-open-pdf">
              <ExternalLink className="w-4 h-4" /> Open PDF
            </a>
            <Button variant="outline" onClick={resetAll} data-testid="uk-guide-reset"><RotateCcw className="w-4 h-4 mr-1.5" /> Reset</Button>
            <Button onClick={save} disabled={saving} data-testid="uk-guide-save">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          {TEXT_SECTIONS.map((s) => (
            <div key={s.title} className="bg-white border border-border rounded-xl p-5">
              <h2 className="text-sm font-bold uppercase tracking-wide text-primary mb-3">{s.title}</h2>
              <div className="space-y-3">
                {s.keys.map((f) => (
                  <div key={f.k}>
                    <Label>{f.label}</Label>
                    {f.multi ? (
                      <textarea
                        value={content[f.k] || ''}
                        onChange={(e) => setField(f.k, e.target.value)}
                        rows={f.rows || 3}
                        data-testid={`uk-guide-field-${f.k}`}
                        className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    ) : (
                      <Input value={content[f.k] || ''} onChange={(e) => setField(f.k, e.target.value)} data-testid={`uk-guide-field-${f.k}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {REPEATABLE.map((r) => (
            <div key={r.key} className="bg-white border border-border rounded-xl p-5" data-testid={`uk-guide-list-${r.key}`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold uppercase tracking-wide text-primary">{r.title}</h2>
                <Button variant="outline" size="sm" onClick={() => addItem(r.key, r.shape)} data-testid={`uk-guide-add-${r.key}`}><Plus className="w-4 h-4 mr-1" /> Add row</Button>
              </div>
              <div className="space-y-2">
                {(content[r.key] || []).map((it, i) => (
                  <div key={i} className="border border-border rounded-lg p-3 space-y-2 bg-gray-50/50">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-foreground/60 uppercase">Row {i + 1}</span>
                      <button onClick={() => removeItem(r.key, i)} className="p-1 rounded hover:bg-red-50 text-red-600" data-testid={`uk-guide-remove-${r.key}-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    {r.cols.map((c) => (
                      <div key={c}>
                        <Label className="text-xs">{c}</Label>
                        {c === 'detail' || c === 'body' || c === 'note' ? (
                          <textarea
                            value={it[c] || ''}
                            onChange={(e) => setListItem(r.key, i, c, e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        ) : (
                          <Input value={it[c] || ''} onChange={(e) => setListItem(r.key, i, c, e.target.value)} className="text-sm" />
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="bg-white border border-border rounded-xl p-5" data-testid="uk-guide-list-docs_items">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold uppercase tracking-wide text-primary">Documentation checklist</h2>
              <Button variant="outline" size="sm" onClick={addDocItem}><Plus className="w-4 h-4 mr-1" /> Add item</Button>
            </div>
            <div className="space-y-2">
              {(content.docs_items || []).map((v, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-5 text-xs font-mono text-foreground/40">{i + 1}.</span>
                  <Input value={v} onChange={(e) => setDocItem(i, e.target.value)} className="flex-1 text-sm" />
                  <button onClick={() => removeDocItem(i)} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white border border-border rounded-xl p-3 sticky top-24">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-foreground/60 uppercase">Live preview</span>
              <span className="text-[11px] text-foreground/40">Refreshes on Save</span>
            </div>
            <div className="aspect-[210/297] bg-gray-100 rounded-lg overflow-hidden border border-border">
              <iframe key={previewKey} title="UK guide preview" src={previewUrl} className="w-full h-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaymanToUKGuideEditor;
